import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { informationGatheringGetEligibilityOptions, informationGatheringStoreSubmissionMutation } from '@visiba/api-vcs-client-v2';
import { useCallback, useMemo } from 'react';
import {
	type InformationGatheringDraft,
	type InformationGatheringPatientIdentity,
	toInformationGatheringSubmissionInput,
} from './information-gathering.model';
import { createInformationGatheringEntryState, type InformationGatheringEntry } from './information-gathering-navigation';

export type InformationGatheringBeginResult =
	| { kind: 'bypass' }
	| { kind: 'collect'; state: ReturnType<typeof createInformationGatheringEntryState> }
	| { kind: 'blocked'; reason: 'nhs-login-required' | 'ineligible' | 'error' };

export function useInformationGathering() {
	const enabled = useFeatureFlag('PatientWeb.NHSAppPilot');
	const queryClient = useQueryClient();
	const { mutateAsync: storeSubmission } = useMutation(informationGatheringStoreSubmissionMutation());

	const begin = useCallback(
		async (entry: InformationGatheringEntry): Promise<InformationGatheringBeginResult> => {
			if (!enabled) return { kind: 'bypass' };

			try {
				const eligibility = await queryClient.fetchQuery({
					...informationGatheringGetEligibilityOptions(),
					retry: false,
				});
				return eligibility?.IsEligible === true
					? { kind: 'collect', state: createInformationGatheringEntryState(entry) }
					: { kind: 'blocked', reason: 'ineligible' };
			} catch (error) {
				return {
					kind: 'blocked',
					reason: isNhsLoginRequiredError(error) ? 'nhs-login-required' : 'error',
				};
			}
		},
		[enabled, queryClient],
	);

	const submit = useCallback(
		async (
			entry: InformationGatheringEntry,
			draft: InformationGatheringDraft,
			patientIdentity: InformationGatheringPatientIdentity,
		): Promise<string | null> => {
			try {
				const result = await storeSubmission({
					body: toInformationGatheringSubmissionInput(draft, entry.role, patientIdentity, entry.destination),
				});
				return result.SubmissionId;
			} catch {
				return null;
			}
		},
		[storeSubmission],
	);

	return useMemo(() => ({ begin, submit }), [begin, submit]);
}

function isNhsLoginRequiredError(error: unknown): boolean {
	return typeof error === 'object' && error !== null && 'status' in error && error.status === 403;
}
